import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AdminRow {
  user_id: string;
  email: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  is_confirmed: boolean;
}

export function useAdminList() {
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(() => Number(localStorage.getItem("admins-page-size")) || 10);
  const setPageSize = (v: number) => { setPageSizeState(v); localStorage.setItem("admins-page-size", String(v)); };

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const { data: roles, count, error } = await supabase
      .from("user_role")
      .select("user_id, role", { count: "exact" })
      .in("role", ["admin", "super_admin"])
      .range(from, to)
      .order("user_id");

    if (error || !roles) {
      setLoading(false);
      return;
    }

    setTotal(count ?? 0);

    const userIds = roles.map((r) => r.user_id);

    // Fetch profiles and emails in parallel
    const [profilesRes, emailsRes] = await Promise.all([
      supabase
        .from("user_admin_profile")
        .select("user_id, first_name, last_name")
        .in("user_id", userIds),
      supabase.rpc("get_admin_emails", { user_ids: userIds }),
    ]);

    const profileMap = new Map(
      (profilesRes.data ?? []).map((p) => [p.user_id, p])
    );
    const emailMap = new Map(
      (emailsRes.data ?? []).map((e: { user_id: string; email: string; is_confirmed: boolean }) => [e.user_id, e])
    );

    const rows: AdminRow[] = roles.map((r) => {
      const p = profileMap.get(r.user_id);
      const emailData = emailMap.get(r.user_id);
      return {
        user_id: r.user_id,
        email: emailData?.email ?? "",
        role: r.role,
        first_name: p?.first_name ?? null,
        last_name: p?.last_name ?? null,
        is_confirmed: emailData?.is_confirmed ?? false,
      };
    });

    setAdmins(rows);
    setLoading(false);
  }, [page, pageSize]);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const updateRole = async (userId: string, newRole: string) => {
    const { error } = await supabase
      .from("user_role")
      .update({ role: newRole })
      .eq("user_id", userId);
    if (!error) await fetchAdmins();
    return { error };
  };

  const updateProfile = async (userId: string, firstName: string, lastName: string) => {
    const { error } = await supabase
      .from("user_admin_profile")
      .upsert({ user_id: userId, first_name: firstName, last_name: lastName });
    if (!error) await fetchAdmins();
    return { error };
  };

  const deleteAdmin = async (userId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-admin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );
      const result = await res.json();
      if (!res.ok) return { error: new Error(result.error) };
      await fetchAdmins();
      return { error: null };
    } catch (e: any) {
      return { error: e };
    }
  };

  return {
    admins, loading, total, page, setPage, pageSize, setPageSize, totalPages,
    refetch: fetchAdmins, updateRole, updateProfile, deleteAdmin,
  };
}
